import { Button, Icon, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import { Address } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { FieldSource, FlightSegment } from '@prisma/client';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { Link } from 'react-router-dom';

import { useLatestAppliedDraft } from '../hooks/requestDraft/useLatestAppliedDraft';
import { AddressMatchTag } from './RequestDrafts/BookingConfirmation/AddressMatchTag';
import { AwbMatchTag } from './RequestDrafts/BookingConfirmation/AwbMatchTag';
import { readBookingPayload } from './RequestDrafts/BookingConfirmation/payload';

type Props = {
  flightSegments: Pick<FlightSegment, 'details_source'>[] | undefined;
  quoteId?: number;
};

/**
 * What an applied draft put on this quote, on the request page where the work happens: that the flight details came
 * from an email instead of a person, and whether the AWB and delivery address the email printed match what the quote
 * holds. Rendered wherever flights are shown, not only on the quote variant: the apply writes to the QUOTE's flight
 * path (`quote_id`).
 */
export const RequestDraftNote = ({ flightSegments, quoteId }: Props) => {
  const bg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const accentColor = useColorModeValue('purple.600', 'purple.300');
  const bodyColor = useColorModeValue('gray.600', 'gray.400');

  const hasAiFilledSegment = !!flightSegments?.some((segment) => segment.details_source === FieldSource.Ai);
  const appliedDraft = useLatestAppliedDraft(quoteId);

  const isApplied = !!appliedDraft;
  if (!hasAiFilledSegment && !isApplied) return null;

  const booking = appliedDraft ? readBookingPayload(appliedDraft.payload) : null;
  const printedAwb = booking?.awb ?? null;
  const printedAddress = booking?.deliveryAddress ?? null;
  const quoteAwb = appliedDraft?.quote?.awb_number ?? null;
  const quoteAddress = (appliedDraft?.quote?.delivery_address as Address | null) ?? null;

  // An apply that wrote only quote columns (AWB, cut-off, address) leaves no AI-filled leg behind.
  const title = hasAiFilledSegment ? 'Filled by AI' : 'AI draft';
  const sender = appliedDraft?.email_from ? ` from ${appliedDraft.email_from}` : '';
  const description = hasAiFilledSegment
    ? 'Flight details entered from a booking confirmation email.'
    : `Received${sender}.`;

  return (
    <Stack direction="row" align="center" spacing={2} py={1.5} px={4} bgColor={bg} borderTopWidth={1}>
      <Icon as={HiOutlineSparkles} boxSize={4} color={accentColor} />
      <Text textStyle="bodySmallRegular" fontWeight="medium" color={accentColor} whiteSpace="nowrap">
        {title}
      </Text>
      <Text textStyle="bodySmallRegular" color={bodyColor} noOfLines={1}>
        {description}
      </Text>

      <Stack direction="row" align="center" spacing={2} color={bodyColor}>
        <AwbMatchTag label="AWB" extractedAwb={printedAwb} quoteAwb={quoteAwb} />
        <AddressMatchTag label="Address" printedAddress={printedAddress} quoteAddress={quoteAddress} />
      </Stack>

      <Show if={isApplied}>
        <Button
          as={Link}
          to={`/ai-inbox?draft=${appliedDraft?.id}`}
          size="xs"
          variant="outline"
          flexShrink={0}
          ml="auto"
        >
          View source email
        </Button>
      </Show>
    </Stack>
  );
};
